import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Input, Select, message, Tooltip, Popconfirm, Dropdown, Menu, Switch, Spin, Upload, Image } from 'antd';
import { SendOutlined, PlusOutlined, MessageOutlined, DoubleRightOutlined, DoubleLeftOutlined, EllipsisOutlined, PictureOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { getModelApiConfig } from '../features/setting/ApiManage';
import axios from '../api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './chat.css';
import ChatHistoryList from './ChatHistoryList';
import ChatMessageList from './ChatMessageList';

// 生成唯一ID
const uuid = () => '_' + Math.random().toString(36).slice(2, 10) + Date.now();

const DEFAULT_WELCOME = 'Hello，今天也想"开挂"？别怕，AI博士来帮您搞定。\n\n知识库问答、Deepseek、Gemini';

const getDefaultConversation = (agent = null) => ({
  id: uuid(),
  title: '',
  messages: [ { role: 'system', content: agent?.welcomeText || DEFAULT_WELCOME } ],
  updatedAt: Date.now(),
});

// 生成带命名空间的 localStorage 键名
const getLocalStorageKey = (baseKey, namespace = 'default', agentId = null) => {
  if (namespace === 'default' && !agentId) {
    return baseKey;
  }
  
  let finalNamespace = namespace;
  if (agentId && namespace !== 'default') {
    finalNamespace = `${namespace}_${agentId}`;
  } else if (agentId) {
    finalNamespace = `agent_${agentId}`;
  }
  
  return finalNamespace === 'default' ? baseKey : `${baseKey}_${finalNamespace}`;
};

const LOCAL_KEY = 'ai_chat_conversations_v1';
const LOCAL_CUR = 'ai_chat_current_id_v1';
const LOCAL_MODEL = 'ai_chat_model_v1';
const LOCAL_KB = 'ai_chat_kb_v1';
const LOCAL_SIDEBAR = 'ai_chat_sidebar_v1';

const ChatPanel = ({
  historyList: externalHistoryList,
  onHistorySelect: externalOnHistorySelect,
  welcomeText: externalWelcomeText,
  onSendMessage: externalOnSendMessage,
  renderInputArea,
  renderToolbar,
  agentType,
  customStyles,
  renderHistoryList,
  renderMessageList,
  agentList = [],
  currentAgent = null,
  onAgentChange,
  namespace = 'default', // 新增：命名空间参数，用于隔离不同功能的聊天历史
  ...restProps
} = {}) => {
  // 获取当前智能体ID
  const agentId = currentAgent?.id;

  // 1. useState全部设为"空"或默认
  const [conversations, setConversations] = useState([]);
  const [currentId, setCurrentId] = useState('');
  const [input, setInput] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [model, setModel] = useState('');
  const [modelApiMap, setModelApiMap] = useState({});
  const [kb, setKb] = useState('none');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sending, setSending] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const chatListRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  // 新增：图片上传相关状态
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  // 新增：智能模型选择相关状态
  const [autoModelSelection, setAutoModelSelection] = useState(false);
  const [recommendedModel, setRecommendedModel] = useState('');
  // 新增：知识库相关状态
  const [kbOptions, setKbOptions] = useState([
    { value: 'none', label: '无知识库' }
  ]);
  const [kbLoading, setKbLoading] = useState(false);

  // 2. useEffect监听currentAgent?.id和namespace，主动加载所有状态
  useEffect(() => {
    if (!currentAgent?.id) {
      const def = getDefaultConversation(currentAgent);
      setConversations([def]);
      setCurrentId(def.id);
      setModel('');
      setKb('none');
      setSidebarCollapsed(false);
      setInput('');
      return;
    }
    const currentAgentId = currentAgent.id;
    // conversations
    const currentLocalKey = getLocalStorageKey(LOCAL_KEY, namespace, currentAgentId);
    const local = localStorage.getItem(currentLocalKey);
    let arr = [getDefaultConversation(currentAgent)];
    if (local) {
      try {
        arr = JSON.parse(local);
        arr = arr.filter(c => c && c.id && c.messages && c.messages.length);
        if (!arr.length) arr = [getDefaultConversation(currentAgent)];
      } catch {}
    }
    setConversations(arr);
    // currentId
    const currentLocalCur = getLocalStorageKey(LOCAL_CUR, namespace, currentAgentId);
    const cur = localStorage.getItem(currentLocalCur);
    setCurrentId(cur && arr.some(c => c.id === cur) ? cur : arr[0].id);
    // model
    const currentLocalModel = getLocalStorageKey(LOCAL_MODEL, namespace, currentAgentId);
    setModel(localStorage.getItem(currentLocalModel) || '');
    // kb
    const currentLocalKb = getLocalStorageKey(LOCAL_KB, namespace, currentAgentId);
    setKb(localStorage.getItem(currentLocalKb) || 'none');
    // sidebar
    const currentLocalSidebar = getLocalStorageKey(LOCAL_SIDEBAR, namespace, currentAgentId);
    const savedSidebar = localStorage.getItem(currentLocalSidebar);
    setSidebarCollapsed(savedSidebar ? JSON.parse(savedSidebar) : false);
    setInput('');
  }, [currentAgent?.id, namespace]);

  // 智能模型推荐功能
  const recommendModel = useCallback((userInput, hasImages = false) => {
    if (!modelOptions.length) return '';
    
    const input = userInput.toLowerCase();
    const hasImagesFlag = hasImages || uploadedImages.length > 0;
    
    // 模型能力映射 - 基于厂商名称
    const modelCapabilities = {
      'openai': {
        name: 'OpenAI',
        capabilities: ['多模态', '图片理解', '创意写作', '代码生成', '复杂推理'],
        keywords: ['图片', '图像', '照片', '创意', '写作', '故事', '小说', '代码', '编程', '算法', '复杂', '推理', '分析']
      },
      'deepseek': {
        name: 'DeepSeek',
        capabilities: ['代码生成', '技术问答', '数学计算', '逻辑推理'],
        keywords: ['代码', '编程', '算法', '技术', '数学', '计算', '逻辑', '开发', '调试', '函数', '类', 'API']
      },
      'gemini': {
        name: 'Gemini',
        capabilities: ['多模态', '图片理解', '创意写作', '实时信息'],
        keywords: ['图片', '图像', '照片', '创意', '写作', '实时', '最新', '新闻', '信息', '搜索']
      },
      'qwen': {
        name: 'Qwen',
        capabilities: ['中文理解', '创意写作', '知识问答', '对话'],
        keywords: ['中文', '汉语', '写作', '创意', '知识', '问答', '对话', '聊天', '故事']
      },
      'moonshot': {
        name: 'Moonshot',
        capabilities: ['长文本处理', '文档理解', '知识问答'],
        keywords: ['长文本', '文档', '报告', '论文', '知识', '问答', '分析', '总结']
      }
    };
    
    // 计算每个模型的匹配分数
    const modelScores = {};
    
    modelOptions.forEach(option => {
      if (option.value === 'auto') return; // 跳过自动选择选项
      
      const modelType = option.originalModel || option.value.split('_')[0]; // 使用originalModel或从value中提取
      const vendorName = option.vendor || '';
      const modelName = option.modelName || '';
      
      // 根据模型类型匹配能力
      const capability = modelCapabilities[modelType];
      if (!capability) return;
      
      let score = 0;
      
      // 关键词匹配
      capability.keywords.forEach(keyword => {
        if (input.includes(keyword)) {
          score += 2;
        }
      });
      
      // 图片处理能力
      if (hasImagesFlag && capability.capabilities.includes('多模态')) {
        score += 5;
      }
      
      // 代码相关
      if (input.includes('代码') || input.includes('编程') || input.includes('函数')) {
        if (capability.capabilities.includes('代码生成')) {
          score += 3;
        }
      }
      
      // 创意写作
      if (input.includes('创意') || input.includes('写作') || input.includes('故事')) {
        if (capability.capabilities.includes('创意写作')) {
          score += 2;
        }
      }
      
      // 中文内容
      if (/[\u4e00-\u9fa5]/.test(input)) {
        if (capability.capabilities.includes('中文理解')) {
          score += 1;
        }
      }
      
      // 长文本处理
      if (input.length > 100) {
        if (capability.capabilities.includes('长文本处理')) {
          score += 2;
        }
      }
      
      modelScores[option.value] = score; // 使用option.value作为key
    });
    
    // 找到最高分数的模型
    let bestModel = '';
    let maxScore = 0;
    
    Object.entries(modelScores).forEach(([modelType, score]) => {
      if (score > maxScore) {
        maxScore = score;
        bestModel = modelType;
      }
    });
    
    // 如果没有匹配的模型，返回第一个非自动选择的模型
    const firstNonAutoModel = modelOptions.find(opt => opt.value !== 'auto');
    return bestModel || (firstNonAutoModel?.value || '');
  }, [modelOptions, uploadedImages]);

  // 监听输入变化，自动推荐模型
  useEffect(() => {
    if (autoModelSelection && input.trim()) {
      const recommended = recommendModel(input);
      setRecommendedModel(recommended);
    }
  }, [input, autoModelSelection, recommendModel]);

  // 获取后端已配置模型列表
  useEffect(() => {
    async function fetchModels() {
      try {
        const res = await axios.get('/api/modelapis/');
        window.__apis = res.data;
        
        // 只显示已配置的模型，过滤掉无效配置
        const validApis = res.data.filter(api => 
          api.model && 
          api.apikey && 
          (api.base_url || api.model_name) // 至少要有base_url或model_name
        );
        
        if (validApis.length === 0) {
          setModelOptions([]);
          setModel('');
          setModelApiMap({});
          message.warning('暂无可用的AI模型，请先在API接口管理中配置模型');
          return;
        }
        
        // 修改模型选项格式：大模型名称 + model name
        const opts = validApis.map(api => {
          // 根据model字段映射大模型名称
          const modelMap = {
            'openai': 'OpenAI',
            'deepseek': 'DeepSeek', 
            'gemini': 'Gemini',
            'qwen': '通义千问',
            'moonshot': 'Moonshot'
          };
          const modelDisplay = modelMap[api.model] || api.model;
          const modelName = api.model_name_description || api.model_name || api.model;
          const displayName = `${modelDisplay} - ${modelName}`;
          // 使用更唯一的value，包含model和model_name
          const uniqueValue = `${api.model}_${api.model_name || api.model}`;
          return { 
            value: uniqueValue, 
            label: displayName,
            vendor: modelDisplay,
            modelName: modelName,
            originalModel: api.model, // 保存原始的model值用于API调用
            apiData: api // 保存完整的API数据
          };
        });
        
        // 添加自动选择选项
        const autoOption = { 
          value: 'auto', 
          label: '自动选择', 
          disabled: false 
        };
        const allOptions = [autoOption, ...opts];
        
        setModelOptions(allOptions);
        
        // 强制默认选中自动选择
        setModel('auto');
        setAutoModelSelection(true);
        
        // 建立model到API对象的映射
        const map = {};
        validApis.forEach(api => { map[api.model] = api; });
        setModelApiMap(map);
        
        console.log(`已加载 ${validApis.length} 个可用模型`);
      } catch (err) {
        setModelOptions([]);
        setModel('');
        setModelApiMap({});
        message.error('获取可用大模型失败: ' + (err.response?.data?.detail || err.message));
      }
    }
    fetchModels();
  }, []);

  // 获取知识库列表
  useEffect(() => {
    async function fetchKnowledgeBases() {
      setKbLoading(true);
      try {
        const response = await axios.get('/api/dify-datasets/', {
          params: {
            page: 1,
            limit: 100, // 获取更多知识库
            include_all: false
          }
        });
        
        const knowledgeBases = response.data.results || [];
        
        // 构建知识库选项
        const kbOpts = [
          { value: 'none', label: '无知识库' }
        ];
        
        knowledgeBases.forEach(kb => {
          kbOpts.push({
            value: kb.id,
            label: kb.name,
            description: kb.description
          });
        });
        
        setKbOptions(kbOpts);
        console.log(`已加载 ${knowledgeBases.length} 个知识库`);
      } catch (error) {
        console.error('获取知识库列表失败:', error);
        // 保持默认的无知识库选项
        setKbOptions([{ value: 'none', label: '无知识库' }]);
      } finally {
        setKbLoading(false);
      }
    }
    
    fetchKnowledgeBases();
  }, []);

  // 模型选择处理
  const handleModelChange = useCallback((value) => {
    setModel(value);
    if (value === 'auto') {
      setAutoModelSelection(true);
      // 根据当前输入推荐模型
      if (input.trim()) {
        const recommended = recommendModel(input);
        setRecommendedModel(recommended);
      }
    } else {
      setAutoModelSelection(false);
      setRecommendedModel('');
    }
  }, [input, recommendModel]);

  // 切换历史对话
  const handleSelectConv = useCallback((id) => {
    setCurrentId(id);
    setInput('');
  }, []);

  // 新建聊天
  const handleNewChat = useCallback(() => {
    const newConv = getDefaultConversation(currentAgent);
    setConversations(prev => [newConv, ...prev]);
    setCurrentId(newConv.id);
    setInput('');
    message.info('已新建对话');
  }, [currentAgent]);

  // 图片上传处理
  const handleImageUpload = useCallback(async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB！');
      return false;
    }

    try {
      // 创建FormData
      const formData = new FormData();
      formData.append('image', file);
      
      // 上传到服务器
      const response = await axios.post('/api/upload-chat-image/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        // 将相对URL转换为完整的后端地址
        // 使用相对路径，通过 Nginx 代理
        const backendUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const fullImageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${backendUrl}${response.data.url}`;
        
        const imageData = {
          uid: file.uid,
          name: file.name,
          url: fullImageUrl, // 使用完整URL用于显示
          serverUrl: fullImageUrl, // 保存完整URL用于发送给模型
          filename: response.data.filename,
          status: 'done'
        };
        setUploadedImages(prev => [...prev, imageData]);
        message.success('图片上传成功');
      } else {
        message.error('图片上传失败');
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      message.error('图片上传失败: ' + (error.response?.data?.error || '未知错误'));
    }
    
    return false; // 阻止默认上传行为
  }, []);

  // 删除图片
  const handleRemoveImage = useCallback((uid) => {
    setUploadedImages(prev => prev.filter(img => img.uid !== uid));
  }, []);

  // 预览图片
  const handlePreviewImage = useCallback((image) => {
    setPreviewImage(image.url);
    setImagePreviewVisible(true);
  }, []);

  // 发送消息（包含智能模型选择）
  const handleSend = useCallback(async () => {
    if (!input.trim() && uploadedImages.length === 0) {
      message.warning('请输入内容或上传图片');
      return;
    }
    setSending(true);
    // 创建AbortController
    const controller = new AbortController();
    setAbortController(controller);
    // 判断是否为新会话的第一次提问
    const currentConvIndex = conversations.findIndex(c => c.id === currentId);
    const isFirstUserMsg =
      conversations[currentConvIndex]?.messages?.length === 1 &&
      conversations[currentConvIndex]?.messages[0]?.role === 'system';
    
    // 构建消息内容
    let messageContent = input.trim();
    let messagesToSend = (conversations.find(c => c.id === currentId)?.messages || []).concat({ role: 'user', content: messageContent });
    
    // 如果有图片，添加到消息中
    if (uploadedImages.length > 0) {
      const imageUrls = uploadedImages.map(img => img.serverUrl).join('\n');
      messageContent = messageContent ? `${messageContent}\n\n图片：\n${imageUrls}` : `图片：\n${imageUrls}`;
      
      // 更新消息内容
      messagesToSend = messagesToSend.map((msg, index) => 
        index === messagesToSend.length - 1 
          ? { ...msg, content: messageContent }
          : msg
      );
    }
    
    // 智能选择模型
    let selectedModel = model;
    if (model === 'auto' || autoModelSelection) {
      const recommended = recommendModel(input, uploadedImages.length > 0);
      selectedModel = recommended;
      if (recommended && recommended !== model) {
        const selectedOption = modelOptions.find(opt => opt.value === recommended);
        const displayName = selectedOption ? selectedOption.label : recommended;
        message.info(`已自动选择 ${displayName} 模型`);
      }
    }
    
    // 追加到当前会话
    setConversations(convs => {
      const updated = convs.map(c =>
        c.id === currentId
          ? { ...c, messages: [...c.messages, { role: 'user', content: messageContent, images: uploadedImages }], updatedAt: Date.now() }
          : c
      );
      return updated;
    });

    // 清空输入和图片
    setInput('');
    setUploadedImages([]);

    // 动态选择API配置
    let apiConf = {};
    let apiUrl = '';
    let modelName = selectedModel;
    let apiKey = '';
    
    if (selectedModel !== 'auto') {
      // 从选中的模型选项中获取API配置
      const selectedOption = modelOptions.find(opt => opt.value === selectedModel);
      if (selectedOption && selectedOption.apiData) {
        apiConf = selectedOption.apiData;
        apiUrl = apiConf.base_url ? apiConf.base_url + '/chat/completions' : '';
        modelName = apiConf.model_name || apiConf.model;
        apiKey = apiConf.apikey;
      } else {
        // 兼容旧格式
        apiConf = getModelApiConfig()[selectedModel] || {};
        apiUrl = apiConf.baseURL ? apiConf.baseURL + '/chat/completions' : '';
        modelName = apiConf.model || selectedModel;
        apiKey = apiConf.apiKey;
      }
    } else {
      // 自动选择模式，使用推荐模型的配置
      const recommendedOption = modelOptions.find(opt => opt.value === recommendedModel);
      if (recommendedOption && recommendedOption.apiData) {
        apiConf = recommendedOption.apiData;
        apiUrl = apiConf.base_url ? apiConf.base_url + '/chat/completions' : '';
        modelName = apiConf.model_name || apiConf.model;
        apiKey = apiConf.apikey;
      }
    }

    // 新会话第一次提问时插入system提示词
    if (isFirstUserMsg) {
      messagesToSend = [
        { role: 'system', content: '你是一个AI助手。请在每次回答后，单独用"主题：xxxx"总结本次对话主题（不超过8字），并换行显示在最后一行。不要把主题内容混在主回答里。' },
        ...messagesToSend
      ];
    }

    // 如果没有选择知识库，直接使用大模型回答
    if (apiUrl) {
      try {
        // 检查是否选择了知识库
        if (kb && kb !== 'none') {
          // 使用知识库聊天API
          const chatResponse = await axios.post('/api/chat-with-knowledge-base/', {
            messages: messagesToSend,
            knowledge_base_id: kb,
            model_config: {
              model_name: modelName,
              base_url: (apiConf.base_url || apiConf.baseURL || apiUrl.replace(/\/chat\/completions\/?$/, '')).replace(/\/$/, ''),
            }
          });
          
          if (chatResponse.data.success) {
            const aiContent = chatResponse.data.response;
            
            // 追加AI回复到当前会话
            setConversations(convs => {
              const updated = convs.map(c =>
                c.id === currentId
                  ? { ...c, messages: [...c.messages, { role: 'assistant', content: aiContent }], updatedAt: Date.now() }
                  : c
              );
              return updated.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
            });
            
            // 显示知识库来源信息
            if (chatResponse.data.sources && chatResponse.data.sources.length > 0) {
              message.info(`已从知识库中找到 ${chatResponse.data.sources.length} 个相关文档`);
            }
          } else {
            // 知识库聊天失败，回退到直接聊天
            message.warning('知识库检索失败，使用直接聊天模式');
            await performDirectChat();
          }
        } else {
          // 直接使用大模型聊天
          await performDirectChat();
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('请求已取消');
        } else {
          console.error('发送消息失败:', err);
          message.error('发送消息失败: ' + err.message);
        }
      } finally {
        setSending(false);
        setAbortController(null);
      }
    } else {
      message.error('未配置API地址');
      setSending(false);
      setAbortController(null);
    }
    
    // 直接聊天函数（走后端代理，避免浏览器 CORS）
    async function performDirectChat() {
      const baseUrl = (apiConf.base_url || apiConf.baseURL || apiUrl.replace(/\/chat\/completions\/?$/, '') || '').replace(/\/$/, '');
      const chatResponse = await axios.post(
        '/api/direct-chat/',
        {
          messages: messagesToSend,
          model_name: modelName,
          base_url: baseUrl,
        },
        { signal: controller.signal },
      );

      if (!chatResponse.data?.success) {
        throw new Error(chatResponse.data?.error || '聊天请求失败');
      }

      const aiContent = chatResponse.data.response || '';
      setConversations(convs => {
        const updated = convs.map(c =>
          c.id === currentId
            ? { ...c, messages: [...c.messages, { role: 'assistant', content: aiContent }], updatedAt: Date.now() }
            : c
        );
        return updated.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      });

      const totalTokens = chatResponse.data.usage?.total_tokens;
      if (totalTokens > 0 && apiKey) {
        try {
          await axios.post('/api/tokenusages/', {
            apikey: apiKey,
            tokens: totalTokens,
            prompt: messageContent.slice(0, 200),
          });
        } catch (err) {
          console.error('记录token使用情况失败:', err.response?.data || err);
        }
      }
    }
  }, [input, uploadedImages, conversations, currentId, model, autoModelSelection, recommendModel, modelApiMap, kb, modelApiMap, currentAgent, namespace, externalOnSendMessage]);

  // 当前会话对象
  const currentConv = conversations.find(c => c.id === currentId) || conversations[0] || { messages: [] };

  // 取消发送
  const handleCancelSend = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
  }, [abortController]);

  // 新增删除会话方法
  const handleDeleteConv = useCallback((id) => {
    setConversations(prev => {
      const filtered = prev.filter(c => c.id !== id);
      // 删除后如果当前会话被删，自动切换到第一个
      if (currentId === id && filtered.length > 0) {
        setCurrentId(filtered[0].id);
      }
      // 更新localStorage
      const currentAgentId = currentAgent?.id;
      const currentLocalKey = getLocalStorageKey(LOCAL_KEY, namespace, currentAgentId);
      localStorage.setItem(currentLocalKey, JSON.stringify(filtered));
      return filtered.length ? filtered : [getDefaultConversation(currentAgent)];
    });
  }, [currentId, namespace, currentAgent]);

  const [hoveredId, setHoveredId] = useState(null);

  // 监听用户手动滚动，取消自动滚动
  useEffect(() => {
    const el = chatListRef.current;
    if (!el) return;
    const handleScroll = () => {
      // 距底部小于30px才认为在底部，否则视为用户手动滚动
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 30) {
        setAutoScroll(false);
      } else {
        setAutoScroll(true);
      }
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // 极致细腻的防回闪平滑滚动到底部
  const scrollAnimating = useRef(false);
  const lastScrollHeight = useRef(0);
  const smoothScrollToBottom = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const duration = 500; // ms
    const start = el.scrollTop;
    const end = el.scrollHeight - el.clientHeight;
    if (Math.abs(end - start) < 2) return;
    scrollAnimating.current = true;
    const startTime = performance.now();
    function step(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      el.scrollTop = start + (end - start) * ease;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.scrollTop = end;
        scrollAnimating.current = false;
        lastScrollHeight.current = el.scrollHeight;
      }
    }
    requestAnimationFrame(step);
  }, []);

  // 只在内容高度变大时触发滚动，动画进行中不重复触发
  useEffect(() => {
    if (!sending || !autoScroll) return;
    const el = chatListRef.current;
    if (!el) return;
    const curHeight = el.scrollHeight;
    if (curHeight > lastScrollHeight.current && !scrollAnimating.current) {
      smoothScrollToBottom();
    }
    lastScrollHeight.current = curHeight;
  }, [conversations, sending, autoScroll, smoothScrollToBottom]);

  // 用户每次发送消息后恢复自动滚动
  useEffect(() => {
    if (!sending) setAutoScroll(true);
  }, [sending]);

  const showLoading = currentConv && currentConv.messages && currentConv.messages.some(
    m => m.role === 'assistant' && (!m.content || m.content.trim() === '')
  );

  // 聊天历史数据源，优先用外部传入
  const historyList = externalHistoryList || conversations;
  // 聊天历史选择回调，优先用外部传入
  const handleHistorySelect = externalOnHistorySelect || handleSelectConv;
  // 欢迎语/提示词，优先用外部传入
  const welcomeText = externalWelcomeText || DEFAULT_WELCOME;
  // 发送消息逻辑，优先用外部传入
  const handleSendMsg = externalOnSendMessage || handleSend;

  // 智能体下拉选项
  const agentOptions = agentList.map(agent => ({
    value: agent.id,
    label: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {agent.icon && <span style={{ marginRight: 8 }}>{agent.icon}</span>}
        {agent.name}
      </span>
    )
  }));
  // 当前智能体欢迎语
  const agentWelcome = currentAgent?.welcomeText || welcomeText;

  // 渲染输入区域（包含图片上传）
  const renderInputAreaWithImages = () => (
    <div>
      {/* 文本输入区域 */}
      <Input.TextArea
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="询问任何问题"
        variant="borderless"
        autoSize={{ minRows: 2, maxRows: 8 }}
        style={{
          fontSize: 17,
          background: 'transparent',
          boxShadow: 'none',
          outline: 'none',
          resize: 'none',
          border: 'none',
          padding: 0,
          marginBottom: 12,
          minHeight: 60,
          maxHeight: 200,
        }}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
            e.preventDefault();
            if (input.trim() || uploadedImages.length > 0) handleSend();
          }
        }}
        disabled={sending}
      />
      
      {/* 图片预览区域 */}
      {uploadedImages.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '8px', 
          marginBottom: 12,
          padding: '8px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          {uploadedImages.map((image) => (
            <div key={image.uid} style={{ position: 'relative' }}>
              <img
                src={image.url}
                alt={image.name}
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0'
                }}
                onClick={() => handlePreviewImage(image)}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ff4d4f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px'
                }}
                onClick={() => handleRemoveImage(image.uid)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // 渲染模型选择下拉框
  const renderModelSelect = () => {
    // 如果没有可用模型，不显示选择器
    if (modelOptions.length === 0) {
      return null;
    }
    
    return (
      <Select
        className="chat-yellow-select"
        value={model}
        onChange={handleModelChange}
        options={modelOptions}
        placeholder="选择模型"
        style={{
          borderRadius: 6,
          border: 'none',
          minWidth: 140,
          height: 32,
          marginRight: 8,
          fontSize: 14,
          color: '#231a17',
        }}
        dropdownStyle={{ borderRadius: 8 }}
        suffixIcon={autoModelSelection && recommendedModel ? <ThunderboltOutlined style={{ color: '#52c41a' }} /> : undefined}
        title={autoModelSelection && recommendedModel ? `推荐模型: ${modelApiMap[recommendedModel]?.vendor_name || '未知厂商'} - ${modelApiMap[recommendedModel]?.model_name_description || modelApiMap[recommendedModel]?.model_name || recommendedModel}` : undefined}
      />
    );
  };

  return (
    <div style={{ 
      display: 'flex', 
      height: '100%', 
      background: '#f7f7f8',
      ...customStyles 
    }}>
      {/* 侧边栏 */}
      {!sidebarCollapsed && (
        <div style={{ 
          width: 260, 
          background: '#fff', 
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* 新建聊天按钮 */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNewChat}
              style={{ width: '100%', height: 40, borderRadius: 8 }}
            >
              新建对话
            </Button>
          </div>
          
          {/* 历史对话列表 */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {renderHistoryList ? renderHistoryList({
              conversations,
              currentId,
              onSelect: handleSelectConv,
              ...restProps
            }) : (
              <ChatHistoryList
                conversations={conversations}
                currentId={currentId}
                onSelect={handleSelectConv}
              />
            )}
          </div>
        </div>
      )}
      
      {/* 主聊天区域 */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative'
      }}>
        {/* 侧边栏切换按钮 */}
        <div style={{ 
          position: 'absolute', 
          top: 16, 
          left: sidebarCollapsed ? 16 : 276,
          zIndex: 10
        }}>
          <Button
            type="text"
            icon={sidebarCollapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ 
              background: '#fff', 
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        </div>

        {/* 聊天内容区域 */}
        {currentConv && currentConv.messages && currentConv.messages.length === 1 && currentConv.messages[0].role === 'system' ? (
          // 空状态：显示欢迎信息
          <div style={{ 
            width: '100%', 
            maxWidth: 900, 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto',
            padding: '40px 20px'
          }}>
            <div style={{ 
              fontSize: 24, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 16,
              textAlign: 'center'
            }}>
              {currentConv.messages[0]?.content || 'Hello，今天也想"开挂"？别怕，AI博士来帮您搞定。'}
            </div>
            {/* 空状态下的输入区域 */}
            <div style={{ 
              width: '100%', 
              maxWidth: 600, 
              background: '#fff', 
              borderRadius: 16, 
              boxShadow: '0 2px 16px #e0e0e0', 
              padding: '16px', 
              display: 'flex', 
              flexDirection: 'column', 
              border: '1px solid #eee' 
            }}>
              {renderInputArea ? renderInputArea({
                input, setInput, onSend: handleSend, sending, uploadedImages, setUploadedImages, ...restProps
              }) : renderInputAreaWithImages()}
              {/* 底部工具栏：模型选择、知识库选择、发送按钮 */}
              {renderToolbar ? renderToolbar({
                model, setModel, kb, setKb, ...restProps
              }) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* 模型选择 */}
                    {renderModelSelect()}
                    {/* 知识库选择 */}
                    <Select
                      className="chat-yellow-select"
                      value={kb}
                      onChange={setKb}
                      options={kbOptions}
                      placeholder={kbLoading ? "加载中..." : "知识库"}
                      loading={kbLoading}
                      style={{
                        borderRadius: 6,
                        border: 'none',
                        minWidth: 120,
                        height: 32,
                        marginRight: 8,
                        fontSize: 14,
                        color: '#231a17',
                      }}
                      dropdownStyle={{ borderRadius: 8 }}
                      notFoundContent={kbLoading ? "加载中..." : "暂无知识库"}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* 图片上传按钮 */}
                    <Upload
                      accept="image/*"
                      beforeUpload={handleImageUpload}
                      showUploadList={false}
                      disabled={sending}
                    >
                      <Button
                        type="text"
                        icon={<PictureOutlined />}
                        style={{
                          width: 44,
                          height: 44,
                          fontSize: 18,
                          color: '#666',
                          border: '1px solid #e0e0e0',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="上传图片"
                      />
                    </Upload>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<SendOutlined />}
                      onClick={sending ? handleCancelSend : handleSend}
                      loading={sending}
                      style={{
                        width: 44,
                        height: 44,
                        fontSize: 20,
                        boxShadow: 'none',
                        background: '#222',
                        border: 'none',
                        marginLeft: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // 有聊天内容时：保持现有布局
          <>
            {/* 聊天消息区独立滚动 */}
            <div ref={chatListRef} style={{ 
              width: '100%', 
              maxWidth: 900, 
              flex: 1, 
              minHeight: 300,
              overflowY: 'auto', 
              margin: '0 auto', 
              paddingBottom: 24, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center' 
            }}>
              {/* loading 提示 */}
              {showLoading && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '32px 0 12px 0', minHeight: 40 }}>
                  <Spin size="small" tip="AI思考中..." />
                </div>
              )}
              <div style={{ width: '100%', maxWidth: 900 }}>
                {renderMessageList ? renderMessageList({
                  messages: currentConv && currentConv.messages ? currentConv.messages.slice(1) : [],
                  showLoading,
                  chatListRef,
                  ...restProps
                }) : (
                  <ChatMessageList
                    messages={currentConv && currentConv.messages ? currentConv.messages.slice(1) : []}
                    showLoading={showLoading}
                    chatListRef={chatListRef}
                  />
                )}
              </div>
            </div>
            {/* 底部输入区 ChatGPT风格+模型/知识库选择 */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', background: 'none', margin: '0 auto', position: 'relative', bottom: 0 }}>
              <div style={{ width: '100%', maxWidth: 900, margin: '24px auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #e0e0e0', padding: '16px', display: 'flex', flexDirection: 'column', border: '1px solid #eee' }}>
                {/* 输入内容区域 */}
                {renderInputArea ? renderInputArea({
                  input, setInput, onSend: handleSend, sending, uploadedImages, setUploadedImages, ...restProps
                }) : renderInputAreaWithImages()}
                {/* 底部工具栏：模型选择、知识库选择、发送按钮 */}
                {renderToolbar ? renderToolbar({
                  model, setModel, kb, setKb, ...restProps
                }) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* 模型选择 */}
                      {renderModelSelect()}
                      {/* 知识库选择 */}
                      <Select
                        className="chat-yellow-select"
                        value={kb}
                        onChange={setKb}
                        options={kbOptions}
                        placeholder={kbLoading ? "加载中..." : "知识库"}
                        loading={kbLoading}
                        style={{
                          borderRadius: 6,
                          border: 'none',
                          minWidth: 120,
                          height: 32,
                          marginRight: 8,
                          fontSize: 14,
                          color: '#231a17',
                        }}
                        dropdownStyle={{ borderRadius: 8 }}
                        notFoundContent={kbLoading ? "加载中..." : "暂无知识库"}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* 图片上传按钮 */}
                      <Upload
                        accept="image/*"
                        beforeUpload={handleImageUpload}
                        showUploadList={false}
                        disabled={sending}
                      >
                        <Button
                          type="text"
                          icon={<PictureOutlined />}
                          style={{
                            width: 44,
                            height: 44,
                            fontSize: 18,
                            color: '#666',
                            border: '1px solid #e0e0e0',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="上传图片"
                        />
                      </Upload>
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<SendOutlined />}
                        onClick={sending ? handleCancelSend : handleSend}
                        loading={sending}
                        style={{
                          width: 44,
                          height: 44,
                          fontSize: 20,
                          boxShadow: 'none',
                          background: '#222',
                          border: 'none',
                          marginLeft: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* 图片预览模态框 */}
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: imagePreviewVisible,
          src: previewImage,
          onVisibleChange: (visible) => setImagePreviewVisible(visible),
        }}
      />
    </div>
  );
};

export default ChatPanel; 